import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { FinancialAccount } from "@common/types/financialAccount";
import { AccountInfoExtraction } from "@common/schemas/financialDocument/account-info-extraction.schema";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import {
    InputControl,
    InputRoot,
    InputSlot,
} from "@design-system/Input/InputParts";
import { ArrowDownIcon } from "@icons";
import editableTableStyles from "../accounting/editableTable/EditableTable.module.scss";
import styles from "./ImporterPage.module.scss";
import { emptyToNull } from "./helpers";

export interface AccountInfoChangePayload {
    accountNumber?: string;
    accountName?: string;
}

interface Props {
    selectedFinancialAccount?: FinancialAccount;
    accountInfoExtraction: AccountInfoExtraction | null;
    onAccountChanged: (payload: AccountInfoChangePayload) => void;
    readOnly?: boolean;
}

export const ImporterAccountPickerTrigger: React.FC<Props> = ({
    selectedFinancialAccount,
    accountInfoExtraction,
    onAccountChanged,
    readOnly,
}) => {
    const [value, setValue] = useState("");

    useEffect(() => {
        const displayedValue = selectedFinancialAccount
            ? getAnonymousAccountNumber(
                  selectedFinancialAccount.accountNumberMask ?? "",
                  2,
              )
            : (emptyToNull(accountInfoExtraction?.accountNumber) ??
              accountInfoExtraction?.accountName ??
              "");

        setValue(displayedValue);
    }, [
        selectedFinancialAccount,
        accountInfoExtraction?.accountNumber,
        accountInfoExtraction?.accountName,
    ]);

    const inputBlocked = readOnly || Boolean(selectedFinancialAccount);

    const onClickInput = (e: React.MouseEvent<HTMLInputElement>) => {
        if (!inputBlocked) {
            e.stopPropagation();
        }
    };

    const onChangeInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value);
        },
        [],
    );

    const onBlurInput = useCallback(() => {
        if (inputBlocked) {
            return;
        }

        const originalValue =
            emptyToNull(accountInfoExtraction?.accountNumber) ??
            accountInfoExtraction?.accountName ??
            "";

        if (value === originalValue) {
            return;
        }

        if (/^\d+$/.test(value)) {
            onAccountChanged({
                accountNumber: value,
                accountName: "",
            });
        } else {
            onAccountChanged({
                accountName: value,
                accountNumber: "",
            });
        }
    }, [inputBlocked, onAccountChanged, value, accountInfoExtraction]);

    return (
        <div className={styles.accountPickerTrigger}>
            <InputRoot
                size="xl"
                className={classNames(
                    editableTableStyles.editableTableInput,
                    styles.accountPickerTriggerInput,
                    {
                        [styles.accountPickerTriggerInputBlocked]:
                            inputBlocked && !readOnly,
                    },
                )}
            >
                <InputControl
                    value={value}
                    onChange={onChangeInput}
                    onClick={onClickInput}
                    onBlur={onBlurInput}
                    readOnly={inputBlocked}
                    placeholder={!inputBlocked ? "Type in" : undefined}
                />
                {!readOnly && (
                    <InputSlot side="right">
                        <ArrowDownIcon />
                    </InputSlot>
                )}
            </InputRoot>
        </div>
    );
};
